import { Link } from "@tanstack/react-router";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Avatar } from "web-core";
import { Tooltip, TooltipContent, TooltipTrigger } from "web-design-system";
import { formatAsPercentage, getInitials } from "creso-core/utils";
import { JOINT_CLIENT_HOLDING_PATTERNS } from "creso-core/constants";
import { ClientHoldingPattern } from "creso-core/types";

export function MemberBar({
  name,
  holdingPattern,
  value,
  percentage,
  client_id,
  display = "value",
}: {
  name: string;
  holdingPattern?: ClientHoldingPattern;
  value?: string;
  percentage?: number;
  client_id?: number;
  display?: "value" | "name";
}) {
  const pct = Math.min(Math.max(Number(percentage ?? 0), 0), 100);
  const label = display === "name" ? name : value;
  const avatar = (
    <span className="shrink-0 cursor-pointer">
      <Avatar fallback={getInitials(name)} size="sm" />
    </span>
  );

  return (
    <div className="flex items-center gap-2 w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          {client_id ? (
            <Link
              to="/clients/$id"
              params={{ id: String(client_id) }}
              className="shrink-0 no-underline!"
              aria-label={`View ${name} goals`}
              target="_blank"
            >
              {avatar}
            </Link>
          ) : (
            avatar
          )}
        </TooltipTrigger>
        <TooltipContent>
          {name}{" "}
          {holdingPattern &&
          JOINT_CLIENT_HOLDING_PATTERNS.includes(holdingPattern)
            ? `(Joint)`
            : null}
        </TooltipContent>
      </Tooltip>
      <div className="relative flex-1 min-w-0 overflow-hidden h-8">
        <div className="absolute inset-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[{ name, value: pct }]}
              layout="vertical"
              barCategoryGap={0}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis type="number" hide domain={[0, 100]} />
              <YAxis type="category" dataKey="name" hide />
              <Bar
                dataKey="value"
                radius={3}
                animationDuration={450}
                animationEasing="ease-out"
                fill="var(--color-secondary)"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="relative flex items-baseline gap-1.5 px-3 py-1.5 h-full">
          <span className="text-sm font-semibold truncate">
            {label}
            {holdingPattern &&
            JOINT_CLIENT_HOLDING_PATTERNS.includes(holdingPattern)
              ? ` (Joint)`
              : null}
          </span>
          {percentage != null ? (
            <span className="text-sm text-muted-foreground">
              ({formatAsPercentage(percentage, "0", 0)})
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
